import { useState, useRef } from 'react';
import { Bug, X, Loader2, Paperclip, Image, Film, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../lib/axios';
import { useAuthStore } from '../store/authStore';
import { useOrgStore } from '../store/orgStore';
import type { ApiResponse } from '@maios/shared';

const CATEGORIES = [
  { value: 'BUG', label: '버그' },
  { value: 'FEATURE_REQUEST', label: '기능 요청' },
  { value: 'QUESTION', label: '질문' },
];

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', 'video/quicktime'];
const MAX_FILES = 3;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

export default function BugReportWidget() {
  const user = useAuthStore((s) => s.user);
  const { activeOrgId, organizations } = useOrgStore();
  const activeOrg = organizations.find((o) => o.id === activeOrgId);

  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('BUG');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setCategory('BUG');
    setTitle('');
    setDescription('');
    setFiles([]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    const valid = selected.filter((f) => {
      if (!ALLOWED_TYPES.includes(f.type)) { toast.error(`${f.name}: 이미지 또는 동영상만 첨부 가능합니다.`); return false; }
      if (f.size > MAX_FILE_SIZE) { toast.error(`${f.name}: 20MB 이하만 첨부 가능합니다.`); return false; }
      return true;
    });
    setFiles((prev) => [...prev, ...valid].slice(0, MAX_FILES));
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeFile = (index: number) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('category', category);
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('pageUrl', window.location.href);
      formData.append('userAgent', navigator.userAgent);
      files.forEach((f) => formData.append('files', f));

      await api.post<ApiResponse>('/bug-reports', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('문제가 접수되었습니다. 감사합니다!');
      resetForm();
      setOpen(false);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '제출에 실패했습니다.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const isImage = (type: string) => type.startsWith('image/');

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors w-full">
        <Bug className="size-3.5 shrink-0" />
        문제 신고
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl mx-4 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
              <h2 className="text-base font-semibold text-gray-900">문제 신고</h2>
              <button onClick={() => { setOpen(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <X className="size-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* 보고자 정보 (자동) */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="text-sm font-medium text-gray-500 mb-1">보고자 정보 (자동 제출)</p>
                <div className="flex items-center gap-3 text-gray-700">
                  <span>{user?.name}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-500">{user?.email}</span>
                </div>
                {activeOrg && (
                  <p className="text-sm text-gray-400 mt-1">조직: {activeOrg.name}</p>
                )}
              </div>

              {/* 카테고리 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">카테고리</label>
                <div className="flex gap-2">
                  {CATEGORIES.map((c) => (
                    <button key={c.value} type="button" onClick={() => setCategory(c.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        category === c.value ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}>{c.label}</button>
                  ))}
                </div>
              </div>

              {/* 제목 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">제목</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="간단히 요약해주세요" required minLength={5} maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
              </div>

              {/* 설명 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">상세 설명</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="어떤 문제가 발생했나요? 재현 방법을 알려주세요." required minLength={10} maxLength={2000} rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                <p className="mt-1 text-sm text-gray-400 text-right">{description.length}/2000</p>
              </div>

              {/* 파일 첨부 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  첨부파일 <span className="text-gray-400 font-normal">({files.length}/{MAX_FILES})</span>
                </label>

                {files.length > 0 && (
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {files.map((f, i) => (
                      <div key={i} className="relative group">
                        {isImage(f.type) ? (
                          <img src={URL.createObjectURL(f)} alt={f.name}
                            className="size-16 object-cover rounded-lg border border-gray-200" />
                        ) : (
                          <div className="size-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                            <Film className="size-5 text-gray-400" />
                          </div>
                        )}
                        <button type="button" onClick={() => removeFile(i)}
                          className="absolute -top-1.5 -right-1.5 size-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="size-3" />
                        </button>
                        <p className="text-[11px] text-gray-400 truncate w-16 text-center mt-0.5">{f.name}</p>
                      </div>
                    ))}
                  </div>
                )}

                {files.length < MAX_FILES && (
                  <button type="button" onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors w-full justify-center">
                    <Paperclip className="size-4" />
                    <Image className="size-4" />
                    <Film className="size-4" />
                    <span className="ml-1">이미지 / 동영상 첨부 (최대 20MB)</span>
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={handleFileSelect} className="hidden" />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setOpen(false); resetForm(); }}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">취소</button>
                <button type="submit" disabled={sending || title.trim().length < 5 || description.trim().length < 10}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5">
                  {sending && <Loader2 className="size-4 animate-spin" />}
                  제출
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
